import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Trash2, Plus, Link, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isShortenedLink(url) {
  return /goo\.gl|maps\.app\.goo\.gl/i.test(url);
}

function parseGoogleMapsUrl(url) {
  const patterns = [
    /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /@(-?\d+\.?\d*),(-?\d+\.?\d*),\d+\.?\d*z/,
    /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
    /\/place\/[^/]+\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /center=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
  }
  return null;
}

async function resolveShortLink(url) {
  try {
    const res = await fetch(url, { mode: 'no-cors', redirect: 'follow' });
    if (res.url && res.url !== url) return res.url;
    const res2 = await fetch(url, { redirect: 'follow' });
    return res2.url || null;
  } catch {
    return null;
  }
}

export default function HRLocations() {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', latitude: '', longitude: '' });
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [urlError, setUrlError] = useState(null);
  const [resolvingLink, setResolvingLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/hr/login'); return; }
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'hr') { navigate('/hr/login'); return; }
    setUser(parsedUser);
    fetchLocations();
  }, [navigate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleManualCoordChange = (field, value) => {
    if (field === 'latitude' && value.includes(',')) {
      const parts = value.split(',').map(s => s.trim());
      if (parts.length === 2) {
        const parsedLat = parseFloat(parts[0]);
        const parsedLng = parseFloat(parts[1]);
        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          setFormData(prev => ({ ...prev, latitude: parts[0].trim(), longitude: parts[1].trim() }));
          if (parsedLat >= -90 && parsedLat <= 90 && parsedLng >= -180 && parsedLng <= 180) {
            toast.success('Both coordinates detected and filled!');
          }
          return;
        }
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUrlPaste = async (url) => {
    setGoogleMapsUrl(url);
    setUrlError(null);

    if (!url.trim()) return;

    // Try direct parsing first
    const coords = parseGoogleMapsUrl(url);
    if (coords) {
      setFormData(prev => ({ ...prev, latitude: String(coords.lat), longitude: String(coords.lng) }));
      toast.success(`Coordinates extracted: ${coords.lat}, ${coords.lng}`);
      return;
    }

    // If it's a shortened link, resolve client-side
    if (isShortenedLink(url)) {
      setResolvingLink(true);
      const resolved = await resolveShortLink(url);
      setResolvingLink(false);

      if (resolved) {
        const resolvedCoords = parseGoogleMapsUrl(resolved);
        if (resolvedCoords) {
          setFormData(prev => ({ ...prev, latitude: String(resolvedCoords.lat), longitude: String(resolvedCoords.lng) }));
          toast.success(`Coordinates extracted: ${resolvedCoords.lat}, ${resolvedCoords.lng}`);
          return;
        }
      }
      // Resolution failed
      setUrlError('shortened');
      return;
    }

    // Not a shortened link but no coords found
    setUrlError('no_coords');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Please enter location name'); return; }
    if (!formData.latitude || !formData.longitude) { toast.error('Please enter latitude and longitude'); return; }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Invalid coordinates. Latitude: -90 to 90, Longitude: -180 to 180');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/locations`, { name: formData.name, latitude: lat, longitude: lng }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Location added successfully');
      setFormData({ name: '', latitude: '', longitude: '' });
      setGoogleMapsUrl('');
      setUrlError(null);
      setShowAddForm(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/locations/${locationId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/hr/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white border-b-2 border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <img src="/anmol-logo.jpg" alt="Advanced Anmol Metcomp Pvt Ltd" className="h-12" />
            <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium" data-testid="locations-logout-button">Logout</button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="locations-title">Approved Locations</h1>
              <p className="text-sm text-zinc-500 font-medium mt-1">{user.name}</p>
            </div>
          </div>
          <div className="flex gap-4 mt-6 flex-wrap">
            <button onClick={() => navigate('/hr/dashboard')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-dashboard-from-locations">Attendance</button>
            <button onClick={() => navigate('/hr/employees')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-employees-from-locations">Employees</button>
            <button onClick={() => navigate('/hr/locations')} className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm" data-testid="nav-locations-active">Locations</button>
            <button onClick={() => navigate('/hr/reports')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-reports-from-locations">Reports</button>
            <button onClick={() => navigate('/hr/notifications')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-notifications-from-locations">Notifications</button>
            <button onClick={() => navigate('/hr/tracking')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-tracking-from-locations">Tracking</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <button onClick={() => setShowAddForm(!showAddForm)} className="h-12 px-6 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-800 active:scale-95 transition-all flex items-center gap-2" data-testid="add-location-button">
            <Plus size={20} /> Add New Location
          </button>
        </div>

        {showAddForm && (
          <div className="bg-white border-2 border-zinc-100 rounded-lg p-6 mb-6">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-zinc-900 mb-4" data-testid="add-location-form-title">New Location</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Location Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors" placeholder="e.g., Main Office, Site A" data-testid="location-name-input" disabled={loading} />
              </div>

              {/* Google Maps URL input */}
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">
                  <Link size={14} className="inline mr-1 mb-0.5" />
                  Paste Google Maps Link (optional)
                </label>
                <input
                  type="text"
                  value={googleMapsUrl}
                  onChange={(e) => handleUrlPaste(e.target.value)}
                  className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                  placeholder="Paste any Google Maps link here..."
                  data-testid="location-url-input"
                  disabled={loading || resolvingLink}
                />
                {resolvingLink && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-zinc-500 font-medium" data-testid="resolving-link-indicator">
                    <Loader2 size={16} className="animate-spin" /> Resolving link, please wait...
                  </div>
                )}
                {!resolvingLink && urlError === 'shortened' && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800" data-testid="url-error-shortened">
                    <p className="font-bold mb-1">We tried to resolve this link automatically but couldn't.</p>
                    <p className="mb-1">Try one of these instead:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Open the link in your browser, let Google Maps load fully, copy the URL from the address bar, paste it here</li>
                      <li>Or copy the coordinates directly from Google Maps and paste them in the fields below</li>
                    </ol>
                  </div>
                )}
                {!resolvingLink && urlError === 'no_coords' && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800" data-testid="url-error-no-coords">
                    <p className="font-bold">Could not extract coordinates from this link.</p>
                    <p className="text-xs mt-1">Try copying the full URL from your browser address bar after the map loads, or enter coordinates manually below.</p>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-sm p-4">
                <p className="text-sm font-bold text-blue-900 mb-2">How to Get Coordinates:</p>
                <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Open Google Maps on phone/computer</li>
                  <li>Search for your location</li>
                  <li>Long-press (mobile) or Right-click (desktop) on the exact spot</li>
                  <li>Copy the coordinates shown (e.g., 28.6139, 77.2090)</li>
                  <li>Paste into the Latitude field below — both values will auto-fill</li>
                </ol>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Latitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.latitude}
                    onChange={(e) => handleManualCoordChange('latitude', e.target.value)}
                    className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                    placeholder="28.6139"
                    data-testid="location-latitude-input"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Longitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.longitude}
                    onChange={(e) => handleManualCoordChange('longitude', e.target.value)}
                    className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                    placeholder="77.2090"
                    data-testid="location-longitude-input"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="h-12 px-6 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50" data-testid="submit-location-button" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Location'}
                </button>
                <button type="button" onClick={() => { setShowAddForm(false); setUrlError(null); setGoogleMapsUrl(''); }} className="h-12 px-6 bg-white border-2 border-zinc-200 text-zinc-900 font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900 active:scale-95 transition-all" data-testid="cancel-add-location-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((location, idx) => (
            <div key={location.id} className="bg-white border-2 border-zinc-100 rounded-lg p-6 hover:border-zinc-300 transition-colors" data-testid={`location-card-${idx}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={20} className="text-zinc-600" />
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-zinc-900" data-testid={`location-name-${idx}`}>{location.name}</h3>
                  </div>
                  <div className="space-y-1">
                    <p className="font-mono text-sm text-zinc-600" data-testid={`location-coords-${idx}`}>Lat: {location.latitude.toFixed(6)}</p>
                    <p className="font-mono text-sm text-zinc-600">Lng: {location.longitude.toFixed(6)}</p>
                    <p className="text-xs text-zinc-500 mt-2">Radius: {location.radius}m</p>
                  </div>
                </div>
                <button onClick={() => handleDelete(location.id)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-location-${idx}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {locations.length === 0 && (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <MapPin size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium">No locations added yet</p>
            <p className="text-sm text-zinc-400 mt-1">Add locations where employees can mark attendance</p>
          </div>
        )}
      </div>
    </div>
  );
}
